import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetHydratedCallRecordResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import {
  CallRecordTagNotification,
  TagNotificationPayload,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueryClient } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useCallRecordTagNotification } from '@frontend/notifications';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';
import { useAlert } from '@frontend/design-system';

export const useWebsocketCallRecordTagEventSubscription = () => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const {
    setShow,
    show: showPanel,
    context = {} as { sectionFocus?: string },
    panelType,
  } = useSlidePanelShallowStore('setShow', 'show', 'context', 'panelType');
  const { selectedLocationIds } = useAppScopeStore();
  const [pendingViewParams, setPendingViewParams] = useState<CallRecordTagNotification | null>(null);

  const hasAccesstoCallRecordingMap = useMemo(() => {
    return selectedLocationIds.reduce((accessMap, locationId) => {
      accessMap[locationId] = hasSchemaACL(locationId, Permission.CALL_RECORDING_READ);
      return accessMap;
    }, {} as Record<string, boolean>);
  }, [selectedLocationIds]);

  const hasAccesstoCallRecording = useMemo(() => {
    return Object.values(hasAccesstoCallRecordingMap).some((hasAccess) => hasAccess);
  }, [hasAccesstoCallRecordingMap]);

  const callRecordQuery = useScopedQuery({
    queryKey: ['call-record-tag-event-details', pendingViewParams?.callRecordId, pendingViewParams?.locationId],
    queryFn: () =>
      PhoneCallsApi.getCallRecord({
        callRecordId: pendingViewParams!.callRecordId,
        locationId: pendingViewParams!.locationId,
        startTime: pendingViewParams!.startedAt,
        endTime: pendingViewParams!.startedAt,
        includeCallRecording: true,
      }),
    onError: () => {
      alert.error('Error fetching call record data');
    },
    enabled: !!pendingViewParams?.callRecordId,
  });

  useEffect(() => {
    if (callRecordQuery.data?.record) {
      setShow(true, 'callDetails', {
        rows: [{ ...callRecordQuery.data.record, tags: [] }],
        currentRowId: callRecordQuery.data.record.id,
        instanceId: null,
        hasAccessToRecordings: hasAccesstoCallRecording,
        toggleRowSelected: () => {},
        callDetailsQueryKey: callRecordQuery.queryKey,
        optimisticApplyTag,
        optimisticRemoveTag,
        sectionFocus: showPanel ? (context as any).sectionFocus : 'details',
      });
    }
  }, [callRecordQuery.data?.record]);

  const { create: createNotification } = useCallRecordTagNotification({
    onView: async (event) => {
      const { payload: params } = event;
      if (pendingViewParams?.callRecordId === params.callRecordId) {
        callRecordQuery.refetch();
      } else {
        setPendingViewParams(params);
      }
    },
  });

  const handleCallRecordTagEventDetailsUpdate = ({
    callRecordId,
    locationId,
    tagsPayload,
  }: {
    callRecordId: string;
    locationId: string;
    tagsPayload: TagNotificationPayload[];
  }) => {
    const queryKeys = [selectedLocationIds, 'call-record-tag-event-details', callRecordId, locationId];
    const callRecord = queryClient.getQueryData(queryKeys) as GetHydratedCallRecordResponse;

    if (!callRecord?.record || (showPanel && panelType !== 'callDetails')) {
      return;
    }

    const assignedTagIds = tagsPayload.map((tag) => tag.id);
    const updatedTagIds = callRecord.record.tagIds?.length
      ? Array.from(new Set([...callRecord.record.tagIds, ...assignedTagIds]))
      : assignedTagIds;

    queryClient.setQueryData(queryKeys, {
      record: {
        ...callRecord.record,
        tagIds: updatedTagIds,
      },
    });
  };

  const eventHandler = useCallback<GetWebsocketEventHandler<NotificationType.CALL_RECORD_TAG>>(
    (event) => {
      const { params } = event;

      // when view panel is already open, we need to update the call record data
      handleCallRecordTagEventDetailsUpdate({
        callRecordId: params.callRecordId,
        locationId: params.locationId,
        tagsPayload: params.tagsPayload,
      });

      createNotification({
        id: event.id,
        timestamp: Date.now(),
        type: 'call-record-tag',
        payload: params,
        state: {
          paused: false,
          status: 'unread',
        },
      });
    },
    [createNotification]
  );
  return useWebsocketEventSubscription(NotificationType.CALL_RECORD_TAG, eventHandler);
};

const optimisticApplyTag = ({
  oldData,
  tag,
  channelId,
}: {
  oldData: GetHydratedCallRecordResponse | undefined;
  tag: Tag;
  channelId: string;
}) => {
  const oldRecord = oldData?.record;
  if (oldRecord) {
    if (oldRecord.channelId === channelId) {
      return {
        record: {
          ...oldRecord,
          tagIds: oldRecord.tagIds ? [...oldRecord.tagIds, tag.id] : [tag.id],
        },
      };
    }
  }
  return undefined;
};

const optimisticRemoveTag = ({
  oldData,
  tag,
  channelId,
}: {
  oldData: GetHydratedCallRecordResponse | undefined;
  tag: Tag;
  channelId: string;
}) => {
  const oldRecord = oldData?.record;
  if (oldRecord) {
    if (oldRecord.channelId === channelId) {
      return {
        record: {
          ...oldRecord,
          tagIds: oldRecord.tagIds ? oldRecord.tagIds.filter((tg) => tg !== tag.id) : [],
        },
      };
    }
  }
  return undefined;
};
